if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      connectedCallback() {
        // The MAIN product form we proxy to. Its Add to cart is what runs the custom
        // subscription widget, the first-order gift and the login gate — so the sticky
        // bar must forward to it rather than add to cart on its own.
        this.mainForm = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');

        this.stickyButton = this.querySelector('[data-sticky-proxy-atc]');
        this.stickyLabel = this.querySelector('[data-sticky-atc-label]');
        this.stickyButtonPrice = this.querySelector('[data-sticky-atc-price]');
        this.stickyPrice = this.querySelector('.f-price');

        this.bindProxyClick();
        this.setupMirror();
        this.setupVisibility();
      }

      disconnectedCallback() {
        if (this.mirrorObserver) this.mirrorObserver.disconnect();
        if (this.observer) this.observer.disconnect();
      }

      // Clicking the sticky button just scrolls the shopper up to the main buy box —
      // it never adds to cart itself. The subscription widget / first-order gift / login
      // gate stay the single add-to-cart path.
      bindProxyClick() {
        if (!this.stickyButton) return;
        this.stickyButton.addEventListener('click', (e) => {
          e.preventDefault();
          const target = (this.mainForm && this.mainForm.querySelector('[name="add"]')) || this.mainForm;
          if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }

      // Copy the main buy box's live label + price into the sticky, and keep them in
      // sync as the subscription widget / variant picker rewrite them.
      setupMirror() {
        this.mirror();

        if (this.mainProductInfo && 'MutationObserver' in window) {
          this.mirrorObserver = new MutationObserver(() => {
            if (this.mirrorScheduled) return;
            this.mirrorScheduled = true;
            requestAnimationFrame(() => {
              this.mirrorScheduled = false;
              this.mirror();
            });
          });
          this.mirrorObserver.observe(this.mainProductInfo, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
          });
        }

        if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
          FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
            requestAnimationFrame(() => this.mirror());
          });
        }
      }

      mirror() {
        if (!this.mainForm) return;

        // Button label + disabled state from the main Add to cart button. Nodes are
        // re-queried each time so the mirror survives the main form re-rendering.
        const mainButton = this.mainForm.querySelector('[name="add"]');
        if (mainButton) {
          // Label = the button's first span; the price lives in a sibling .btn-price span
          // (#CartBtnPrice-...) that the subscription widget / Skio picker rewrite in place.
          const label = mainButton.querySelector('span');
          if (label && this.stickyLabel) this.stickyLabel.textContent = label.textContent;

          if (this.stickyButtonPrice) {
            const mainPrice = mainButton.querySelector('.btn-price, [id^="CartBtnPrice-"]');
            if (mainPrice) {
              this.stickyButtonPrice.textContent = mainPrice.textContent;
              this.stickyButtonPrice.style.display = mainPrice.style.display;
            } else {
              this.stickyButtonPrice.style.display = 'none';
            }
          }

          if (this.stickyButton) {
            const disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
            if (disabled) {
              this.stickyButton.setAttribute('disabled', 'disabled');
            } else {
              this.stickyButton.removeAttribute('disabled');
            }
          }
        }

        // Price from the main buy box (only present on layouts that show a standalone
        // .f-price in the sticky; the compact button-only bar skips this).
        const mainPrice = this.mainProductInfo && this.mainProductInfo.querySelector('.f-price');
        if (mainPrice && this.stickyPrice) {
          this.stickyPrice.classList.toggle('f-price--on-sale', mainPrice.classList.contains('f-price--on-sale'));
          this.stickyPrice.classList.toggle('f-price--sold-out', mainPrice.classList.contains('f-price--sold-out'));
          this.stickyPrice.innerHTML = mainPrice.innerHTML;
        }
      }

      // Reveal the bar only once the main Add to cart has scrolled out of view.
      setupVisibility() {
        if (!this.mainForm || !this.container) {
          if (this.container) this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        this.checkDevice();
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin: '-80px 0px 0px 0px' }
        );
        this.observer.observe(this.mainForm);
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }
    }
  );
}
